import * as mapboxgl from 'mapbox-gl';
import { GeoJSONSource } from 'mapbox-gl';
import Mapbox from 'index.vue';
import { BaseLocationsLayer } from './baseLocationsLayer';
import { localeIncludes } from 'locale-includes';
import * as turf from '@turf/turf';
import destination from '@turf/destination';
import { BaseFeatureLayer } from './baseFeatureLayer';
import i18n from '../../../main';

const QUANTILE_COLORS: Record<number, string> = {
    1: '#b71c1c',
    2: '#d32f2f',
    3: '#e64a19',
    4: '#f57c00',
    5: '#fbc02d',
    6: '#afb42b',
    7: '#689f38',
    8: '#388e3c',
    9: '#1b5e20',
};

// Colors ordered from worst (red) to best (green), used for both the output labels and the legend.
const OUTPUT_LEGEND_COLORS: string[] = Object.keys(QUANTILE_COLORS)
    .map((k) => Number(k))
    .sort((a, b) => a - b)
    .map((k) => QUANTILE_COLORS[k]);

// Use a logarithmic value axis once the (positive) outputs span at least this ratio (max / min);
// below it a linear axis reads more clearly.
const LOG_SCALE_MIN_RATIO = 50;

function hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace('#', '');
    return [parseInt(h.substring(0, 2), 16), parseInt(h.substring(2, 4), 16), parseInt(h.substring(4, 6), 16)];
}

function rgbToHex(r: number, g: number, b: number): string {
    const h = (r << 16) + (g << 8) + b;
    return '#' + ('000000' + h.toString(16)).slice(-6);
}

// Interpolate a color across evenly-spaced stops for t in [0, 1].
// Matches the CSS linear-gradient rendered by the legend (sRGB interpolation),
// so a label's color equals the legend color at that value's position.
function pickGradientColor(colors: string[], t: number): string {
    if (colors.length === 0) return '#9e9e9e';
    if (colors.length === 1) return colors[0];
    const clamped = Math.max(0, Math.min(1, t));
    const scaled = clamped * (colors.length - 1);
    const idx = Math.min(colors.length - 2, Math.floor(scaled));
    const frac = scaled - idx;
    const [r1, g1, b1] = hexToRgb(colors[idx]);
    const [r2, g2, b2] = hexToRgb(colors[idx + 1]);
    return rgbToHex(
        Math.round(r1 + (r2 - r1) * frac),
        Math.round(g1 + (g2 - g1) * frac),
        Math.round(b1 + (b2 - b1) * frac),
    );
}

// Map each distinct value to its rank position in [0, 1] (worst = 0, best = 1) using the
// midrank for ties. Rank-based rather than value-linear so a single outlier (e.g. one store
// performing far above the rest) doesn't squash everything else onto the low end of the scale.
// All values equal -> every value maps to 0.5.
function computeRankFractions(values: number[]): Map<number, number> {
    const map = new Map<number, number>();
    const n = values.length;
    if (n === 0) return map;
    const sorted = [...values].sort((a, b) => a - b);
    let i = 0;
    while (i < n) {
        let j = i;
        while (j + 1 < n && sorted[j + 1] === sorted[i]) j++;
        const midrank = (i + j) / 2;
        map.set(sorted[i], n === 1 ? 0.5 : midrank / (n - 1));
        i = j + 1;
    }
    return map;
}

function drawRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + r, r);
    ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
    ctx.arcTo(x, y + h, x, y + h - r, r);
    ctx.arcTo(x, y, x + r, y, r);
    ctx.closePath();
}

function ensureLabelImage(map: any, color: string, text: string): string {
    const safeText = text.replace(/[^a-zA-Z0-9.,$€£¥+-]/g, '_');
    const name = `output-label-${color.replace('#', '')}-${safeText}`;
    if (map.hasImage(name)) return name;

    const fontSize = 13;
    const padX = 4;
    const padY = 2;
    const radius = 4;
    const border = 1;
    const dpr = 2;

    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d') as CanvasRenderingContext2D;
    measureCtx.font = `bold ${fontSize}px Roboto, Arial, sans-serif`;
    const textWidth = measureCtx.measureText(text).width;

    const innerW = Math.ceil(textWidth + padX * 2);
    const innerH = fontSize + padY * 2;
    const totalW = innerW + border * 2;
    const totalH = innerH + border * 2;

    const canvas = document.createElement('canvas');
    canvas.width = totalW * dpr;
    canvas.height = totalH * dpr;
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D;
    ctx.scale(dpr, dpr);

    drawRoundedRect(ctx, 0, 0, totalW, totalH, radius + border);
    ctx.fillStyle = '#ffffff';
    ctx.fill();

    drawRoundedRect(ctx, border, border, innerW, innerH, radius);
    ctx.fillStyle = color;
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.font = `bold ${fontSize}px Roboto, Arial, sans-serif`;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(text, totalW / 2, totalH / 2 + 0.5);

    const imageData = ctx.getImageData(0, 0, totalW * dpr, totalH * dpr);
    map.addImage(name, imageData, { pixelRatio: dpr });
    return name;
}

function formatOutput(value: number, model: any): string {
    if (value == null || !Number.isFinite(value)) return '';
    const locale = (i18n as any)?.locale || model?.language || 'en-US';
    const outputType = model?.outputType;
    const compact: Intl.NumberFormatOptions = { notation: 'compact', compactDisplay: 'short', maximumFractionDigits: 1 };
    if (outputType === 1 || outputType === 3) {
        try {
            return new Intl.NumberFormat(locale, { style: 'currency', currency: model?.currency || 'EUR', ...compact }).format(value);
        } catch {
            return new Intl.NumberFormat(locale, compact).format(value);
        }
    }
    if (outputType === 2 || outputType === 4) {
        return new Intl.NumberFormat(locale, compact).format(value);
    }
    return new Intl.NumberFormat(locale, compact).format(value);
}

export class LocationsLayer {

    protected mapboxInstance: InstanceType<typeof Mapbox>;
    private locationCirclesLayer;
    private locationCircleLabelsLayer;
    private locationOutputLabelsLayer;
    private locationsLayer;
    constructor(mapboxInstance: InstanceType<typeof Mapbox>) {
        this.mapboxInstance = mapboxInstance;

        this.locationCirclesLayer = new BaseFeatureLayer(mapboxInstance, 'top', 'locations-circles-layer', 'locations-circles-source', 'line', {}, {
            'line-color': '#000000',
            'line-width': 1
        });
        this.locationCircleLabelsLayer = new BaseFeatureLayer(mapboxInstance, 'top', 'locations-circle-labels-layer', 'locations-circle-labels-source', 'symbol', {
            'text-field': ['get', 'label'],
            'text-size': 14,
            'text-anchor': 'center',
            'text-allow-overlap': false,
            'text-ignore-placement': false,
          //  'text-offset': [0, -1.2]
        }, {
            'text-color': '#fff',
            'text-halo-color': '#000',
            'text-halo-width': 100
        });
        this.locationsLayer = new BaseLocationsLayer(mapboxInstance, '', 'locations-layer', 'locations-source', 'symbol', {
            'icon-image':  ['get', 'url'],
            'icon-size': ['/', ['get', 'size'], 1],
            'icon-allow-overlap': true,
            'icon-anchor': 'top-left',
            'icon-offset':
                [-20, ['+', -20, ['get', 'offset']]
                ],

            'symbol-sort-key': ['get', 'zIndex'],
        }, { 'icon-opacity': ['case', ['get', 'inFilter'], 1, 0.4]  }, true);
        this.locationOutputLabelsLayer = new BaseFeatureLayer(mapboxInstance, '', 'locations-output-labels-layer', 'locations-output-labels-source', 'symbol', {
            'icon-image': ['get', 'iconImage'],
            'icon-anchor': 'top-left',
            'icon-offset': [6, -12],
            'icon-allow-overlap': false,
            'icon-ignore-placement': false,
            'icon-padding': 0,
            'symbol-sort-key': ['-', 0, ['get', 'sortKey']],
            'visibility': mapboxInstance.showLocationOutput ? 'visible' : 'none',
        }, {
            'icon-opacity': ['case', ['get', 'inFilter'], 1, 0.4],
        });
        try { this.mapboxInstance.map.moveLayer('locations-output-labels-layer'); } catch {}
        this.mapboxInstance.map.on('styledata', () => {
            try {
                if (this.mapboxInstance.map.getLayer('locations-output-labels-layer')) {
                    this.mapboxInstance.map.moveLayer('locations-output-labels-layer');
                }
            } catch {}
        });
        this.mapboxInstance.map.on('click', 'locations-layer', (e: any) => {
            e.features.sort((a : any, b : any) => (b.properties.zIndex || 0) - (a.properties.zIndex || 0));
            this.mapboxInstance.$emit('locationLeftClick', { x: e.point.x, y: e.point.y, latitude: e.lngLat.lat, longitude: e.lngLat.lng, location: JSON.parse(e.features[0].properties.location) });
        });
        this.mapboxInstance.map.on('contextmenu', 'locations-layer', (e: any) => {
            e.features.sort((a: any, b: any) => (b.properties.zIndex || 0) - (a.properties.zIndex || 0));
            this.mapboxInstance.$emit('locationRightClick', { x: e.point.x, y: e.point.y, latitude: e.lngLat.lat, longitude: e.lngLat.lng, location: JSON.parse(e.features[0].properties.location) });
        });
    }
    
    public SetOutputLabelsVisible = (visible: boolean) => {
        const map = this.mapboxInstance.map;
        if (map && map.getLayer('locations-output-labels-layer')) {
            map.setLayoutProperty('locations-output-labels-layer', 'visibility', visible ? 'visible' : 'none');
        }
    }

    public SelectLocation = async (oldLocationId: any, newLocationId: any) => {
        if (this.mapboxInstance.map.getSource('locations-source')) {
            const source = this.mapboxInstance.map.getSource('locations-source') as GeoJSONSource;
            const geojson = (source as any)._data as GeoJSON.FeatureCollection;
            if (oldLocationId) this.updateIconForLocation(this.features, oldLocationId, false);
            if (newLocationId) this.updateIconForLocation(this.features, newLocationId, true);
            // Load the changed icons (e.g. the newly-selected variant) before setting features,
            // otherwise Mapbox's worker can log "image could not be loaded" for an icon that is
            // still being fetched by a concurrent DataChanged() pass.
            const urls = new Set<string>();
            [oldLocationId, newLocationId].forEach((id: any) => {
                if (id == null) return;
                const f = this.features.find((x: any) => x.properties.location?.id === id);
                if (f?.properties?.url) urls.add(f.properties.url);
            });
            await Promise.all(this.locationsLayer.LoadImages(urls));
            this.locationsLayer.SetFeatures(this.features);
        }
    }
    private updateIconForLocation = (features: any[], locationId: number | null, isSelected: boolean) => {
        const feature = features.find((f: any) => f.properties.location?.id === locationId);
        if (feature) {
            const iconProps = this.locationsLayer.GetLocationIcon(this.mapboxInstance.model, feature.properties.location, isSelected);
            feature.properties = { ...feature.properties, ...iconProps };
        }
    };
   
    private features: any[] = [];
    private lastModelId: any = null;



    public AddCircles = async (location: any) => {
        if (location == null) {
            this.locationCirclesLayer.SetFeatures([]);
            this.locationCircleLabelsLayer.SetFeatures([]);

        }
        else {
            const circles = this.mapboxInstance.model.mapRanges.map(function (mapRange: any) {
                return turf.circle([location.lng, location.lat] as [number, number], mapRange, { steps: 64, units: 'meters' });
            });
            const labels = this.mapboxInstance.model.mapRanges.map(function (mapRange: any) {
                const topPoint = destination([location.lng, location.lat] as [number, number], mapRange, 0, { units: 'meters' });
                return {
                    type: 'Feature',
                    geometry: topPoint.geometry,
                    properties: {
                        label: mapRange + 'M'
                    }
                };
            });
            this.locationCirclesLayer.SetFeatures(circles);
            this.locationCircleLabelsLayer.SetFeatures(labels);
        }
        
    }



    public DataChanged = async (leftPadding: number = 0, rightPadding: number = 0, maintainZoomAndPosition = false) => {
        const modelId = this.mapboxInstance.locations.length == 0 ? null : this.mapboxInstance.locations[0].modelId;

        const uniqueUrls = new Set<string>();
        this.mapboxInstance.locations.forEach((location: any) => {
            const normalUrl = this.locationsLayer.GetLocationIcon(this.mapboxInstance.model, location, false).url;
            const selectedUrl = this.locationsLayer.GetLocationIcon(this.mapboxInstance.model, location, true).url;
            uniqueUrls.add(normalUrl);
            uniqueUrls.add(selectedUrl);
        });
        const loadImagePromises = this.locationsLayer.LoadImages(uniqueUrls);

        const hasFilter = this.mapboxInstance.filter != null && this.mapboxInstance.filter !== "";
        const hasLocationFilter = this.mapboxInstance.filterLocations != null && this.mapboxInstance.filterLocations.length > 0;

        const locationsInFilter: any[] = [];
        this.features = this.mapboxInstance.locations.filter((location: any) => location.visibility !== 2).map((location: any) => {
            const inFilter = hasFilter ?
                localeIncludes(location.name, this.mapboxInstance.filter, { usage: "search", sensitivity: "base" }) || (location.locationGroup != null && localeIncludes(location.locationGroup, this.mapboxInstance.filter, { usage: "search", sensitivity: "base" })) :
                hasLocationFilter ?
                    this.mapboxInstance.filterLocations.some((fid: number) => fid == location.id) :
                    true;
            if (inFilter) locationsInFilter.push(location);
            return {
                
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [location.lng, location.lat] as [number, number],
                },
                properties: {
                    ...this.locationsLayer.GetLocationIcon(this.mapboxInstance.model, location, location.id == this.mapboxInstance.location?.id, false),
                    location: location,
                    title: location.name,
                    inFilter: inFilter
                }
            }
        });
        
        await Promise.all(loadImagePromises);
        this.locationsLayer.SetFeatures(this.features);

        const model = this.mapboxInstance.model;
        // Only strictly-positive outputs drive the scale. Negatives are shown as solid red and left
        // out here (a log axis needs positive values, and a big negative shouldn't distort the scale);
        // zero / null outputs stay hidden as before.
        const outputs = this.features
            .map((f: any) => f.properties.location.output)
            .filter((o: any) => o != null && o > 0);
        const rankFractions = computeRankFractions(outputs);
        if (outputs.length > 0) {
            const sortedOutputs = [...outputs].sort((a, b) => a - b);
            const last = sortedOutputs.length - 1;
            const minOutput = sortedOutputs[0];
            const maxOutput = sortedOutputs[last];
            const range = maxOutput - minOutput;
            // Wide spread -> log axis so the low values aren't crushed against one high outlier.
            const useLog = maxOutput / minOutput >= LOG_SCALE_MIN_RATIO;
            const logMin = Math.log(minOutput);
            const logRange = Math.log(maxOutput) - logMin;
            // Bar x-axis is the output value (left = min, right = max), but each colour is placed at
            // the value position of its rank. This warps the gradient so a store's colour matches the
            // bar at its value: with a high outlier the low-value stores keep their full red->green
            // spread near the left instead of yellow sitting dead-centre.
            const stopCount = Math.min(sortedOutputs.length, 40);
            const stops: string[] = [];
            for (let s = 0; s < stopCount; s++) {
                const rankFraction = stopCount === 1 ? 0.5 : s / (stopCount - 1);
                const value = sortedOutputs[Math.round(rankFraction * last)];
                let position: number;
                if (useLog) position = logRange > 0 ? ((Math.log(value) - logMin) / logRange) * 100 : rankFraction * 100;
                else position = range > 0 ? ((value - minOutput) / range) * 100 : rankFraction * 100;
                stops.push(`${pickGradientColor(OUTPUT_LEGEND_COLORS, rankFraction)} ${position.toFixed(2)}%`);
            }
            // Value at the centre of the bar: the geometric mean on a log axis, the arithmetic mean
            // on a linear axis. Shown as a middle tick between the two ends.
            const midOutput = useLog ? Math.sqrt(minOutput * maxOutput) : (minOutput + maxOutput) / 2;
            const ticks = [formatOutput(minOutput, model), formatOutput(midOutput, model), formatOutput(maxOutput, model)];
            this.mapboxInstance.$emit('outputLegendChanged', {
                gradient: `linear-gradient(to right, ${stops.join(', ')})`,
                ticks,
                colors: OUTPUT_LEGEND_COLORS,
            });
        } else {
            this.mapboxInstance.$emit('outputLegendChanged', null);
        }
        const outputLabelFeatures = this.features
            .filter((f: any) => f.properties.location.output != null && f.properties.location.output !== 0)
            .map((f: any) => {
                const loc = f.properties.location;
                const label = formatOutput(loc.output, model);
                // Positive outputs color by rank position (worst = 0, best = 1) so a single outlier
                // doesn't push every other store onto the red end. Negatives are the worst case and
                // aren't part of the scale, so they get solid red.
                const color = loc.output > 0
                    ? pickGradientColor(OUTPUT_LEGEND_COLORS, rankFractions.get(loc.output) ?? 0.5)
                    : OUTPUT_LEGEND_COLORS[0];
                let impactIndicator = '';
                if (loc.outputParent != null) {
                    const diff = loc.output - loc.outputParent;
                    const denom = Math.abs(loc.outputParent);
                    const relativeImpact = denom !== 0 ? Math.abs(diff) / denom : (diff !== 0 ? Infinity : 0);
                    if (relativeImpact > 0.001) {
                        impactIndicator = diff > 0 ? ' ▲' : ' ▼';
                    }
                }
                const displayLabel = label + impactIndicator;
                const iconImage = ensureLabelImage(this.mapboxInstance.map, color, displayLabel);
                return {
                    type: 'Feature',
                    geometry: f.geometry,
                    properties: {
                        iconImage,
                        inFilter: f.properties.inFilter,
                        sortKey: loc.output,
                    },
                };
            });
        this.locationOutputLabelsLayer.SetFeatures(outputLabelFeatures);
        try { this.mapboxInstance.map.moveLayer('locations-output-labels-layer'); } catch {}

        if (locationsInFilter.length == 1) this.mapboxInstance.$emit('locationLeftClick', { x: 0, y: 0, latitude: 0, longitude: 0, location: locationsInFilter[0] });

        const bounds = new mapboxgl.LngLatBounds();
        let boundsChanged = new mapboxgl.LngLatBounds();
        if (this.mapboxInstance.locations.length > 0) {
            this.mapboxInstance.locations.filter(function (location: any) { return location.visibility != 2 }).map(function (location: any) {
                if (location.changeType > 0) boundsChanged.extend([location.lng, location.lat]);
                bounds.extend([location.lng, location.lat]);
            });
        }
        else {
            //there are no locations so set the bounds to the bounds of the areasets of this model.
            this.mapboxInstance.model.areaSets.forEach((areaSet: any) => {
                bounds.extend([areaSet.bounds.minX, areaSet.bounds.minY]);
                bounds.extend([areaSet.bounds.maxX, areaSet.bounds.maxY]);
            });
            boundsChanged = bounds;
        }
        if (!this.mapboxInstance.report && !maintainZoomAndPosition) {
            const fitOptions = {
                padding: { top: 30, right: rightPadding + 60, left: leftPadding + 60, bottom: 30 },
                maxZoom: 12,
                duration: 1000, // Smooth animation duration in milliseconds
            };
            
            if (this.mapboxInstance.locations != null && this.mapboxInstance.locations.length > 0 && this.lastModelId != modelId) {
                this.mapboxInstance.map.fitBounds(bounds, fitOptions);
            }
            else if (!boundsChanged.isEmpty()) {
                this.mapboxInstance.map.fitBounds(boundsChanged, fitOptions);
            }
            else if (locationsInFilter.length > 1 && locationsInFilter.length != this.mapboxInstance.locations.length) { // Only fit to filter when not everything is in the filter
                const boundsFilter = new mapboxgl.LngLatBounds();
                locationsInFilter.forEach(location => boundsFilter.extend([location.lng, location.lat]));
                this.mapboxInstance.map.fitBounds(boundsFilter, fitOptions);
            }
        }
        this.lastModelId = modelId;
        
    };
}   
